import { useEffect, useState } from 'react';
import { X, Smartphone, Download } from 'lucide-react';

const DISMISS_KEY = 'jilong_pwa_install_dismissed_at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const isStandalone = () =>
  window.matchMedia && window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const wasRecentlyDismissed = () => {
  try {
    const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return t && Date.now() - t < DISMISS_TTL_MS;
  } catch {
    return false;
  }
};

const PWAInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  };

  const install = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    try {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch {
      // user dismissed or error — silently
    }
    setDeferredPrompt(null);
    setVisible(false);
    setInstalling(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed z-[80] right-3 bottom-3 sm:right-6 sm:bottom-6 max-w-[calc(100vw-1.5rem)] sm:max-w-sm animate-in slide-in-from-bottom-5 fade-in duration-300"
      data-testid="pwa-install-banner"
      role="dialog"
      aria-label="Install JILONG di HP Anda"
    >
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#0a3d8f] via-[#1560c9] to-[#0a8f3d]" />
        <button
          type="button"
          onClick={dismiss}
          aria-label="Tutup"
          data-testid="pwa-install-close"
          className="absolute top-2 right-2 p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <X size={16} />
        </button>

        <div className="p-4 pr-8 flex items-start gap-3">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#0a3d8f] to-[#082e6b] flex items-center justify-center shadow-md">
            <Smartphone size={22} className="text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[15px] font-bold text-gray-900 leading-tight">
              Install JILONG di HP Anda
            </p>
            <p className="text-xs text-gray-600 mt-1 leading-snug">
              Akses cepat ke katalog produk, manual, dan tutorial video — langsung dari home screen.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={install}
                disabled={installing}
                data-testid="pwa-install-btn"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-[#0a3d8f] hover:bg-[#082e6b] text-white text-xs font-semibold shadow-sm disabled:opacity-60 transition-colors"
              >
                <Download size={13} />
                {installing ? 'Menginstall…' : 'Install'}
              </button>
              <button
                type="button"
                onClick={dismiss}
                data-testid="pwa-install-later"
                className="px-3 py-2 rounded-md text-xs font-semibold text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
              >
                Nanti saja
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallBanner;
